// Shrinks an image in the browser before upload, so a big logo never fails on
// the server's size limit. Keeps transparency; output stays well under 1 MB.

const MAX_BYTES = 600 * 1024;

const load = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("unreadable"));
    };
    img.src = url;
  });

const toBlob = (canvas, type, quality) => new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const draw = (img, side) => {
  const scale = Math.min(1, side / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
};

// Returns a File ready to upload. Throws only when the browser can't read the image.
export async function optimizeImage(file, { maxSide = 512 } = {}) {
  if (!file?.type?.startsWith("image/")) throw new Error("not-an-image");
  // Names like "Lilly&Mae logo (1).png" become the storage key; keep them plain.
  const base =
    file.name
      .replace(/\.[^.]+$/, "")
      .replace(/[^a-zA-Z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "logo";
  // Small PNG/JPG files go up as they are, under a plain name.
  if (/^image\/(png|jpe?g)$/.test(file.type) && file.size <= 200 * 1024) {
    const ext = file.type === "image/png" ? "png" : "jpg";
    return new File([file], `${base}.${ext}`, { type: file.type });
  }

  const img = await load(file);
  // Step down in size until it fits, but never below half the asked size.
  const sides = [maxSide, Math.round(maxSide * 0.75), Math.round(maxSide / 2)];
  for (const side of sides) {
    const canvas = draw(img, side);
    // WebP keeps transparency and is small; browsers that can't make it return PNG.
    const webp = await toBlob(canvas, "image/webp", 0.9);
    if (webp?.type === "image/webp" && webp.size <= MAX_BYTES) {
      return new File([webp], `${base}.webp`, { type: "image/webp" });
    }
    const png = await toBlob(canvas, "image/png");
    if (png && png.size <= MAX_BYTES) return new File([png], `${base}.png`, { type: "image/png" });
  }
  // Last resort: JPEG on white, always small.
  const canvas = draw(img, sides[sides.length - 1]);
  const flat = document.createElement("canvas");
  flat.width = canvas.width;
  flat.height = canvas.height;
  const ctx = flat.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, flat.width, flat.height);
  ctx.drawImage(canvas, 0, 0);
  const jpg = await toBlob(flat, "image/jpeg", 0.85);
  return new File([jpg], `${base}.jpg`, { type: "image/jpeg" });
}
